import { useCallback, useRef, useState } from "react";

import { useIsomorphicLayoutEffect } from "@app/util/hooks/use-isomorphic-layout-effect";

/**
 * Reports whether a line-clamped element is actually cut off, so a "read more" toggle can stay
 * hidden when the text already fits.
 *
 * Pass the class that applies the clamp. The hook briefly removes it to read the unclamped height
 * and puts it straight back, all in one task, so the browser never paints the unclamped state.
 * Comparing scrollHeight to clientHeight does not work: Chrome discards the clipped lines instead
 * of keeping them as scrollable overflow, so both values stay equal no matter how much is hidden.
 *
 * Pass `content` as well when the element's text can change without the element being replaced,
 * as happens on client-side navigation between two products. The hook measures again.
 *
 * Measuring only happens while the class is on the element. An expanded element always fits, so
 * measuring it would hide the very toggle that expanded it. The last clamped reading is kept.
 *
 * Returns false on the server and on the first client render, so SSR markup and hydration match.
 * The first measurement runs in a layout effect, so the toggle is there in the same paint as
 * hydration rather than one frame later. It cannot appear in the server HTML: only a browser can
 * tell whether the text fits.
 */
export const useIsClamped = <T extends HTMLElement = HTMLElement>(clampClassName: string, content?: string) => {
  const ref = useRef<T>(null);
  const frameId = useRef(0);
  const [isClamped, setIsClamped] = useState(false);

  const measure = useCallback(() => {
    const element = ref.current;

    if (!element || !element.classList.contains(clampClassName)) return;

    const style = window.getComputedStyle(element);
    const clampedHeight = element.clientHeight;

    element.classList.remove(clampClassName);
    const fullHeight = element.scrollHeight;
    element.classList.add(clampClassName);

    // Half the font size as slack: a tight line-height makes the text box a pixel or two taller
    // than its line box, while a hidden line always costs a whole line-height. font-size is used
    // rather than line-height because it always computes to a px value, never to "normal".
    setIsClamped(fullHeight - clampedHeight > parseFloat(style.fontSize) / 2);
  }, [clampClassName]);

  useIsomorphicLayoutEffect(() => {
    if (!clampClassName) {
      // Without a class there is nothing to take off, so the toggle would stay hidden for good.
      // Renaming the class in the SCSS module is how this happens, and CSS module keys are typed
      // as a plain index signature, so the compiler cannot catch it. Fail where a developer looks.
      if (process.env.NODE_ENV !== "production") {
        throw new Error("useIsClamped: no clamp class given, so the clamp cannot be measured.");
      }

      return;
    }

    let cancelled = false;
    const measureUnlessCancelled = () => {
      if (!cancelled) measure();
    };

    measure();

    // A webfont swapping in changes the line count without resizing the clamped box.
    document.fonts?.ready.then(measureUnlessCancelled);

    // Damped per frame, like use-resize-observer: dragging the window edge fires this constantly,
    // and every call forces the browser to lay the element out twice.
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(() => {
            cancelAnimationFrame(frameId.current);
            frameId.current = requestAnimationFrame(measureUnlessCancelled);
          });

    if (ref.current) observer?.observe(ref.current);

    return () => {
      cancelled = true;
      observer?.disconnect();
      cancelAnimationFrame(frameId.current);
    };
    // `content` is not read here: it is the signal that the text changed and needs measuring again.
  }, [measure, content]);

  return [ref, isClamped] as const;
};
